import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Toggle from 'components/toggle';
import PartnerConnectionModal from './partner_connection_modal';
import { get, post, deleteRequest } from 'helpers/api';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import ResyncButton from './resync_button';
import { showSnackbar } from 'components/ui/snackbar/helper';

export default function PartnerToggle({
  allowManualSignups,
  allowNoCredSignups, // Whether or not the partner allows automatic signups
  deregistrationUrl,
  initialShowResync,
  initiallyConnected,
  mechanism,
  partnerName,
  partnerId,
  partnerOnHold,
  premium, // Whether or not the partner is a premium partner
  registrationUrl,
  resyncUrl,
  showResyncUrl,
  userId
}) {
  const [connected, setConnected] = useState(initiallyConnected);
  const [noCredSignup, setNoCredSignup] = useState(premium); // the state of whether or not to use noCredSignup
  const [showModal, setShowModal] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showResyncButton, setShowResyncButton] = useState(initialShowResync);

  function handleToggleClicked() {
    if (mechanism === 'api') {
      setShowModal(true);
    } else if (connected) {
      disconnectPartner();
    } else {
      connectPartner();
    }
  }

  function apiRegistrationParams(credentials) {
    let params = { userId: userId, partnerId: partnerId, noCredSignUp: noCredSignup };
    if (!noCredSignup) params = Object.assign(params, credentials);

    return params;
  }

  const registerApiPartner = async (credentials) => {
    await post(registrationUrl, apiRegistrationParams(credentials)).then((response) => {
      if (response.webAuthUri) {
        window.location.href = response.webAuthUri;
      } else if (response.error) {
        setConnected(false);
        showSnackbar({ text: `There was an error while connecting: ${response.error}`, variant: 'danger' });
      } else {
        showSnackbar({ text: `Successfully connected to ${partnerName}.`, variant: 'success' });
        setConnected(true);
      }
    });
  };

  function baseParams(additionalParams = {}) {
    return {
      syndication_credential: {
        syndication_partner_id: partnerId,
        type: mechanism,
        user_id: userId,
        ...additionalParams
      }
    };
  }

  async function registerFileExportPartner() {
    await post(registrationUrl, baseParams()).then(() => {
      showSnackbar({ text: `Successfully connected to ${partnerName}.`, variant: 'success' });
      setConnected(true);
    }).catch(() => {
      setConnected(false);
      showSnackbar({
        text: `There was an error while connecting to ${partnerName}, please contact support`,
        variant: 'danger'
      });
    });
  }

  function toElement() {
    return document.getElementById(`email-to-${partnerId}`);
  }

  function ccElement() {
    return document.getElementById(`email-cc-${partnerId}`);
  }

  async function registerEmailPartner() {
    const additionalParams = {
      to: toElement().value,
      cc: ccElement().value
    };
    const params = baseParams(additionalParams);

    await post(registrationUrl, params).then(() => {
      showSnackbar({ text: `Successfully connected to ${partnerName}.`, variant: 'success' });
      setConnected(true);
      checkIfShowResyncButton();
      triggerToggleEvent();
    }).catch(() => {
      setConnected(false);
      showSnackbar({
        text: `There was an error while connecting to ${partnerName}. Please contact support`,
        variant: 'danger'
      });
    });
  }

  async function checkIfShowResyncButton() {
    const res = await get(urlWithAppendedParams(showResyncUrl), baseParams());
    setShowResyncButton(res.showResyncButton);
  }

  const connectPartner = async (credentials) => {
    setSendingRequest(true);
    if (mechanism === 'api') {
      await registerApiPartner(credentials);
    } else if (mechanism === 'file_export') {
      await registerFileExportPartner();
    } else if (mechanism === 'email') {
      await registerEmailPartner();
    }
    setSendingRequest(false);
    setShowModal(false);
  };

  const disconnectPartner = async () => {
    setSendingRequest(true);
    let removePartnerRequestData;

    if (mechanism === 'api') {
      removePartnerRequestData = await disconnectApiPartner();
    } else {
      removePartnerRequestData = await disconnectCredential();
    }
    disconnectedSnackbar(removePartnerRequestData);
    if (removePartnerRequestData.success) {
      if (mechanism !== 'api') triggerToggleEvent();
      setConnected(false);
    }
    setSendingRequest(false);
    setShowModal(false);
  };

  // This event is listened to in app/assets/javascripts/otter/users/profile.js to connect the react action to the
  // inputs in the old code.
  const triggerToggleEvent = () => {
    const event = new CustomEvent('partnerToggle', { detail: { partnerId } });
    window.dispatchEvent(event);
  };

  const disconnectApiPartner = async () => {
    const params = { partner_id: partnerId, user_id: userId };
    const response = await deleteRequest(deregistrationUrl, { params });

    return { success: !response.error, errorMessage: response.error };
  };

  function disconnectedSnackbar({ success, errorMessage }) {
    if (success) {
      showSnackbar({ text: `Successfully disconnected from ${partnerName}.`, variant: 'success' });
    } else {
      showSnackbar({ text: `There was an error while disconnecting${errorMessage}`, variant: 'danger' });
    }
  }

  function urlWithAppendedParams(url) {
    const urlObject = new URL(url);
    Object.entries(baseParams().syndication_credential).forEach(([key, value]) => {
      urlObject.searchParams.append(`syndication_credential[${key}]`, value);
    });

    return urlObject.toString();
  }

  function handleResync(res) {
    setShowResyncButton(false);
    toElement().value = res.to;
    ccElement().value = res.cc;
    showSnackbar({ text: `Successfully synced account reps to company settings.`, variant: 'success' });
  }

  async function disconnectCredential() {
    try {
      await deleteRequest(urlWithAppendedParams(deregistrationUrl));
      setShowResyncButton(false);
      return { success: true };
    } catch {
      return { success: false, errorMessage: '. Please Contact Support' };
    }
  }

  function toolTipText() {
    if (partnerOnHold) {
      return `${partnerName} is currently 'On Hold' due to technical issues.
               Once they've been corrected, users may adjust the syndication settings.`;
    }

    return `You are currently ${connected ? '' : 'not '}syndicating your listings with ${partnerName}.`;
  }

  const tooltip = (
    <Tooltip id={`toggle-tooltip-${partnerId}`}>
      {toolTipText()}
    </Tooltip>
  );

  return (
    <div className="d-flex align-items-center gap-2">
      <ResyncButton
        baseParams={baseParams()}
        partnerId={partnerId}
        resyncUrl={resyncUrl}
        showResyncButton={showResyncButton}
        onResync={handleResync}
      />

      <OverlayTrigger overlay={tooltip} placement="bottom">
        <div className="d-flex align-items-center gap-2">
          {partnerOnHold && (
            <FontAwesomeIcon className="ds-text-warning" icon={faTriangleExclamation} size="2x"/>
          )}
          <Toggle
            checked={connected}
            disabled={partnerOnHold || sendingRequest}
            name="partner_toggle"
            showText={false}
            theme="ds"
            onChange={handleToggleClicked}
          />
        </div>
      </OverlayTrigger>
      <PartnerConnectionModal
        allowManualSignups={allowManualSignups}
        allowNoCredSignups={allowNoCredSignups}
        connected={connected}
        modalOpen={showModal}
        noCredSignup={noCredSignup}
        partnerName={partnerName}
        sendingRequest={sendingRequest}
        toggleNoCredRegistration={() => setNoCredSignup(!noCredSignup)}
        onClose={() => setShowModal(false)}
        onConfirm={connected ? disconnectPartner : connectPartner}
      />
    </div>
  );
}

PartnerToggle.propTypes = {
  allowManualSignups: PropTypes.bool.isRequired,
  allowNoCredSignups: PropTypes.bool.isRequired,
  deregistrationUrl: PropTypes.string.isRequired,
  initiallyConnected: PropTypes.bool.isRequired,
  initialShowResync: PropTypes.bool.isRequired,
  mechanism: PropTypes.oneOf(['api', 'file_export', 'email']).isRequired,
  partnerId: PropTypes.number,
  partnerName: PropTypes.string.isRequired,
  partnerOnHold: PropTypes.bool.isRequired,
  premium: PropTypes.bool.isRequired,
  registrationUrl: PropTypes.string.isRequired,
  resyncUrl: PropTypes.string.isRequired,
  showResyncUrl: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired
};
